import { ApiClient } from '@api/api-configuration';
import {
  GenericSearchSelect,
  type ApiFetchFunction,
  type ApiQueryParams,
} from '@app/components/ui/GenericSearchSelect';
import useGlobalInventoryCategories from '@hooks/fetching/useGlobalInventoryCategories';
import { Button, Modal, SelectProps } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { InventoryCategory } from '../../types';
import { InventoryCategoryForm } from '../InventoryCategoryForm/InventoryCategoryForm';

type InventoryCategorySelectProps = {
  // RHF-style value/onChange so this can be dropped straight into the form config
  // as a `custom` field (FormField passes `value`, `onChange` and `error`).
  // An item belongs to at most one category, and `null` is uncategorised.
  value?: number | null;
  onChange?: (categoryId: number | null) => void;
  error?: boolean;
  placeholder?: string;
} & Omit<SelectProps, 'options' | 'onChange' | 'loading' | 'onSearch' | 'value' | 'mode'>;

export const InventoryCategorySelectGeneric = ({
  value,
  onChange,
  error,
  placeholder,
  ...selectProps
}: InventoryCategorySelectProps) => {
  const { t } = useTranslation();
  // Used only to resolve the already-selected id to its label. The dropdown itself is
  // searched/paginated via the API below.
  const { globalInventoryCategories, invalidateGlobalInventoryCategories } = useGlobalInventoryCategories();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // Owned here rather than left to the select: it has to stay closed behind the create
  // form, otherwise the option list pops back over the form once the form closes.
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const fetchCategories: ApiFetchFunction<InventoryCategory> = async (params: ApiQueryParams) =>
    ApiClient.serviceApi.inventory.listInventoryCategories({ ...params, sort: 'name' });

  const handleSelectionChange = useCallback(
    (selection: InventoryCategory | InventoryCategory[] | undefined) => {
      const selected = Array.isArray(selection) ? selection[0] : selection;
      // Clearing has to reach the caller as an explicit null, otherwise an item can
      // never be moved back to uncategorised.
      onChange?.(selected ? selected.categoryId : null);
    },
    [onChange]
  );

  // Provide the object for the currently-selected id so its label renders even before the
  // dropdown is opened (and for a category past the first page of results).
  const selectedItemsProvider = useCallback(() => {
    if (value == null) return undefined;
    return globalInventoryCategories.find((category) => category.categoryId === value);
  }, [value, globalInventoryCategories]);

  const openCreateForm = useCallback(() => {
    setIsCreateOpen(true);
    setDropdownOpen(false);
  }, []);

  const handleDropdownOpenChange = useCallback(
    (open: boolean) => {
      setDropdownOpen(isCreateOpen ? false : open);
    },
    [isCreateOpen]
  );

  const handleCreated = useCallback(
    (category: InventoryCategory) => {
      setIsCreateOpen(false);
      invalidateGlobalInventoryCategories();
      onChange?.(category.categoryId);
    },
    [invalidateGlobalInventoryCategories, onChange]
  );

  // SearchSelectV2 sets its own `popupRender`, so one passed from here would be discarded
  // (antd resolves `popupRender || dropdownRender`). `footerRender` is its supported slot
  // inside the popup, below the option list's divider.
  const footerSlot = {
    footerRender: () => (
      <div className="px-2">
        <Button type="link" size="small" block onClick={openCreateForm}>
          + {t('inventory-category-select-add-new')}
        </Button>
      </div>
    ),
  } as Partial<SelectProps>;

  return (
    <>
      <GenericSearchSelect<InventoryCategory>
        {...selectProps}
        {...footerSlot}
        allowClear
        value={value ?? undefined}
        // Controlled the whole time — see dropdownOpen above.
        open={isCreateOpen ? false : dropdownOpen}
        onOpenChange={handleDropdownOpenChange}
        status={error ? 'error' : undefined}
        apiFetch={fetchCategories}
        getItemId={(category) => category.categoryId}
        getItemLabel={(category) => category.name}
        onSelectionChange={handleSelectionChange}
        selectedItemsProvider={selectedItemsProvider}
        placeholder={placeholder ?? t('inventory-category-select-placeholder')}
        searchPlaceholder={t('inventory-categories-search-placeholder')}
      />

      <Modal
        open={isCreateOpen}
        title={t('inventory-categories-modal-create-title')}
        onCancel={() => setIsCreateOpen(false)}
        footer={null}
        width={420}
        destroyOnHidden
      >
        <InventoryCategoryForm onSubmitted={handleCreated} />
      </Modal>
    </>
  );
};

export default InventoryCategorySelectGeneric;
